import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    Container,
    Divider,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Typography
} from "@mui/material";
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleLeft from '@mui/icons-material/ArrowCircleLeft';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { LoadingButton } from "@mui/lab";
import { useLoginController } from './context/login-controller';
import { useLoginState } from './context/login-context';
import { SearchableSelect } from '../../components/searchable-select';

export function LoginComponent() {
    const {
        handleOnChange,
        toggleShowPassword,
        handleCompanyChange,
        authentication,
        handleBackButton,
        applicationData,
        isCompanyStep,
        validationErrors
    } = useLoginController();

    const {
        preAuthenticationRequest,
        preAuthenticationResponse,
        authenticationRequest,
        showPassword,
        loading
    } = useLoginState();
    const credentialsLocked = isCompanyStep || loading;
    const companyLabel = preAuthenticationRequest.type === 1 ? 'Entidade' : 'Empresa';

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            authentication();
        }
    };

    return (
        <React.Fragment>
            <Box
                component="main"
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                    alignItems: 'center',
                    display: 'flex',
                    flexGrow: 1,
                    minHeight: '100vh'
                }}
            >
                <Container maxWidth={'sm'}>
                    <Card
                        elevation={2}
                        sx={{
                            borderRadius: 3,
                            overflow: 'hidden'
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} sx={{ mb: 2 }}>
                                    <Stack spacing={2} alignItems="center">
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: 'primary.main',
                                                boxShadow: 2
                                            }}
                                        >
                                            <LockOutlinedIcon sx={{ fontSize: 32, color: 'primary.contrastText' }} />
                                        </Box>
                                        <Box textAlign="center">
                                            <Typography
                                                variant="h4"
                                                component="h1"
                                                sx={{
                                                    fontWeight: 700,
                                                    mb: 0.5,
                                                    color: 'text.primary'
                                                }}
                                            >
                                                {applicationData.moduleName}
                                            </Typography>
                                            {applicationData.moduleDescription && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 2 }}
                                                >
                                                    {applicationData.moduleDescription}
                                                </Typography>
                                            )}
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: 'text.secondary',
                                                    mt: 1
                                                }}
                                            >
                                                Entre com suas credenciais para continuar
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sx={{ display: applicationData.moduleId === 1 ? 'block' : 'none' }}
                                >
                                    <InputLabel required sx={{ fontWeight: 500, mb: 1 }}>
                                        Tipo de login
                                    </InputLabel>
                                    <Select
                                        name="select-type"
                                        value={preAuthenticationRequest?.type}
                                        error={!!validationErrors.type}
                                        fullWidth
                                        onChange={handleOnChange}
                                        disabled={credentialsLocked}
                                    >
                                        <MenuItem value={1} key={1}>Entidade</MenuItem>
                                        <MenuItem value={2} key={2}>Empresa</MenuItem>
                                    </Select>
                                    {validationErrors.type && <FormHelperText error>{validationErrors.type}</FormHelperText>}
                                </Grid>

                                <Grid item xs={12}>
                                    <InputLabel required sx={{ fontWeight: 500, mb: 1 }}>
                                        E-mail ou usuário
                                    </InputLabel>
                                    <OutlinedInput
                                        name="input-user"
                                        placeholder="Digite seu e-mail ou usuário"
                                        fullWidth
                                        error={!!validationErrors.user}
                                        value={preAuthenticationRequest?.user}
                                        disabled={credentialsLocked}
                                        onChange={handleOnChange}
                                        onKeyDown={handleKeyDown}
                                        autoComplete="off"
                                        inputProps={{
                                            autoComplete: 'off',
                                            'data-lpignore': 'true',
                                            'data-form-type': 'other',
                                            style: { textTransform: 'none' }
                                        }}
                                    />
                                    {validationErrors.user && <FormHelperText error>{validationErrors.user}</FormHelperText>}
                                </Grid>

                                <Grid item xs={12}>
                                    <InputLabel required sx={{ fontWeight: 500, mb: 1 }}>
                                        Senha
                                    </InputLabel>
                                    <OutlinedInput
                                        name="input-password"
                                        placeholder="Digite sua senha"
                                        value={preAuthenticationRequest?.password}
                                        error={!!validationErrors.password}
                                        fullWidth
                                        onChange={handleOnChange}
                                        onKeyDown={handleKeyDown}
                                        type={showPassword ? 'text' : 'password'}
                                        disabled={credentialsLocked}
                                        autoComplete="new-password"
                                        inputProps={{
                                            autoComplete: 'new-password',
                                            'data-lpignore': 'true',
                                            'data-form-type': 'other',
                                            style: { textTransform: 'none' }
                                        }}
                                        endAdornment={
                                            <InputAdornment position='end'>
                                                <IconButton
                                                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                                    onClick={toggleShowPassword}
                                                    edge="end"
                                                >
                                                    {!showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                    {validationErrors.password && <FormHelperText error>{validationErrors.password}</FormHelperText>}
                                </Grid>

                                {isCompanyStep && (
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <InputLabel required sx={{ fontWeight: 500, mb: 1 }}>
                                            {companyLabel}
                                        </InputLabel>
                                        <SearchableSelect
                                            options={preAuthenticationResponse.companies?.map((element) => ({
                                                id: element.id,
                                                label: `${element.name} - ${element.formattedDocument}`
                                            })) || []}
                                            value={authenticationRequest?.companyId}
                                            onChange={handleCompanyChange}
                                            placeholder={`Pesquisar ${companyLabel.toLowerCase()}...`}
                                            error={!!validationErrors.companyId}
                                            helperText={validationErrors.companyId}
                                            fullWidth
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={authenticationRequest.remember ?? false}
                                                    name="check-remember"
                                                    onChange={handleOnChange}
                                                    disabled={isCompanyStep}
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    Lembrar de mim
                                                </Typography>
                                            }
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                        {isCompanyStep && (
                                            <LoadingButton
                                                loading={loading}
                                                loadingPosition="start"
                                                startIcon={<ArrowCircleLeft />}
                                                onClick={handleBackButton}
                                                variant="outlined"
                                                sx={{ px: 3 }}
                                            >
                                                Voltar
                                            </LoadingButton>
                                        )}
                                        <Box sx={{ flex: isCompanyStep ? '0 0 auto' : '1 1 auto', ml: 'auto' }}>
                                            <LoadingButton
                                                loading={loading}
                                                variant="contained"
                                                loadingPosition="end"
                                                endIcon={
                                                    (applicationData !== undefined && preAuthenticationRequest.type === 2) ||
                                                    (isCompanyStep && preAuthenticationRequest.type === 1)
                                                        ? <CheckCircleIcon />
                                                        : <ArrowCircleRightIcon />
                                                }
                                                onClick={authentication}
                                                fullWidth
                                                sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: '1rem' }}
                                            >
                                                {(applicationData !== undefined && preAuthenticationRequest.type === 2) ||
                                                (isCompanyStep && preAuthenticationRequest.type === 1)
                                                    ? "Entrar"
                                                    : "Próximo"}
                                            </LoadingButton>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        </React.Fragment>
    );
}
